import React from 'react';
import { useTranslation } from 'react-i18next';

import { GuardrailErrorPolicy, GuardrailSettingsState } from '../../../../../../gamma-api';
import IbInput from '../../../../../components/common/IbInput';
import IbNumberInput from '../../../../../components/common/IbNumberInput';
import IbSelect from '../../../../../components/common/IbSelect';
import IbSwitch from '../../../../../components/common/IbSwitch';
import IbTypography from '../../../../../components/common/IbTypography';
import { ITEM_CLASS_NAME } from '../../../../../components/common/TypedStepModal/const';
import { GuardrailCheckSettings, getGuardrailErrorPolicyLabel, isSettingsEnabled } from '../../shared/utils';

export interface ICommonCheckFieldProps {
  check: GuardrailCheckSettings;
  onChange: (patch: Partial<GuardrailCheckSettings>) => void;
}

export interface ICheckEnabledFieldProps {
  state: GuardrailSettingsState;
  onChange: (state: GuardrailSettingsState) => void;
}

export const CheckEnabledField: React.FC<ICheckEnabledFieldProps> = ({ state, onChange }) => {
  const { t } = useTranslation();

  const onEnabledChange = (checked: boolean) => {
    onChange(checked ? GuardrailSettingsState.Enabled : GuardrailSettingsState.Disabled);
  };

  return (
    <div className={ITEM_CLASS_NAME}>
      <IbTypography>
        <IbSwitch checked={isSettingsEnabled(state)} size="small" onChange={onEnabledChange} />
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Step: Enabled: Title')}
        </IbTypography.Paragraph>
      </IbTypography>
    </div>
  );
};

export const CheckNameField: React.FC<ICommonCheckFieldProps> = ({ check, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className={ITEM_CLASS_NAME}>
      <IbTypography.Paragraph strong type="secondary">
        {t('Agent: Guardrails: Step: Name: Title')}
      </IbTypography.Paragraph>
      <IbTypography.Paragraph type="secondary">
        {t('Agent: Guardrails: Step: Name: Description')}
      </IbTypography.Paragraph>
      <IbInput value={check.name ?? ''} onChange={(name) => onChange({ name })} />
    </div>
  );
};

export const CheckBlockMessageField: React.FC<ICommonCheckFieldProps> = ({ check, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className={ITEM_CLASS_NAME}>
      <IbTypography.Paragraph strong type="secondary">
        {t('Agent: Guardrails: Step: BlockMessage: Title')}
      </IbTypography.Paragraph>
      <IbTypography.Paragraph type="secondary">
        {t('Agent: Guardrails: Step: BlockMessage: Description')}
      </IbTypography.Paragraph>
      <IbInput value={check.block_message} onChange={(blockMessage) => onChange({ block_message: blockMessage })} />
    </div>
  );
};

export const CheckErrorPolicyField: React.FC<ICommonCheckFieldProps> = ({ check, onChange }) => {
  const errorPolicyOptions = [GuardrailErrorPolicy.FailClosed, GuardrailErrorPolicy.FailOpen].map((value) => ({
    value,
    label: getGuardrailErrorPolicyLabel(value),
  }));
  const { t } = useTranslation();

  return (
    <div className={ITEM_CLASS_NAME}>
      <IbTypography.Paragraph strong type="secondary">
        {t('Agent: Guardrails: ErrorPolicy: Title')}
      </IbTypography.Paragraph>
      <IbSelect
        options={errorPolicyOptions}
        value={check.on_error}
        onChange={(value) => onChange({ on_error: value as GuardrailErrorPolicy })}
      />
    </div>
  );
};

export const CheckTimeoutField: React.FC<ICommonCheckFieldProps> = ({ check, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className={ITEM_CLASS_NAME}>
      <IbTypography.Paragraph strong type="secondary">
        {t('Agent: Guardrails: Timeout: Title')}
      </IbTypography.Paragraph>
      <IbTypography.Paragraph type="secondary">{t('Agent: Guardrails: Timeout: Description')}</IbTypography.Paragraph>
      <IbNumberInput
        min={0}
        value={check.timeout_seconds ?? undefined}
        onChange={(value) => onChange({ timeout_seconds: value ? Number(value) : null })}
      />
    </div>
  );
};

export const CheckUseOriginalTextForCheckField: React.FC<ICommonCheckFieldProps> = ({ check, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className={ITEM_CLASS_NAME}>
      <IbTypography>
        <IbSwitch
          checked={check.use_original_text_for_check}
          size="small"
          onChange={(useOriginalTextForCheck) => onChange({ use_original_text_for_check: useOriginalTextForCheck })}
        />
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: UseOriginalTextForCheck: Title')}
        </IbTypography.Paragraph>
      </IbTypography>
      <IbTypography.Paragraph type="secondary">
        {t('Agent: Guardrails: UseOriginalTextForCheck: Description')}
      </IbTypography.Paragraph>
    </div>
  );
};
